import {
    HealthPlanCopayRecord,
    HealthPlanLifeRecord,
    HealthPlanParseResult,
} from '../healthPlanTypes';
import {
    extractTextFromPdf,
    loadXlsx,
    readFileAsTextWithFallback,
    toSignedCurrency,
    normalizeUserCode,
    parseBradescoDate,
    findSheet,
    readRows,
    parseCopayFromText,
    parseLives,
    consolidateLives
} from './parserUtils';

// Local consolidateLives removed in favor of shared one.

// NOTE: I am copying `consolidateLives` and `parseLives` logic here as local helpers or better yet, I should have moved them to utils.
// Given the complexity, I will assume for this step I will implement them locally or refactor later.
// To save context tokens, I'll implement the PDF logic first which is the focus.

function isUnimedCopayTxt(text: string): boolean {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return false;
    const sample = lines.slice(0, 5);
    return sample.every(line => (line.match(/;/g) || []).length >= 10);
}

function isUnimedPremiumPdf(textPages: string[]): boolean {
    if (textPages.length === 0) return false;
    const firstPage = textPages[0];
    // Premium report should be "Fatura" or "Mensalidade" or "Analítico", NOT "Utilização"
    const isUtilization = firstPage.includes('Relatório de Utilização');
    if (isUtilization) return false;

    return (
        (firstPage.includes('Unimed') || firstPage.includes('UNIMED') || firstPage.includes('Salutaris')) &&
        (/\d{2}\/\d{2}\/\d{4}/.test(firstPage)) &&
        (firstPage.includes('Relatório') || firstPage.includes('Fatura') || firstPage.includes('Demonstrativo') || /0006\d{13}/.test(firstPage))
    );
}

// function isUnimedCopayPdf removed per user request

function parseUnimedCopayTxt(text: string): HealthPlanCopayRecord[] {
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
    const copayEntries: HealthPlanCopayRecord[] = [];

    for (const line of lines) {
        const cols = line.split(';');
        if (cols.length < 17) continue;

        const userCode = cols[2]?.trim();
        const beneficiaryName = cols[3]?.trim();
        const providerName = cols[5]?.trim();
        const dateRaw = cols[8]?.trim();
        const procedureCode = cols[12]?.trim();
        const valueRaw = cols[16]?.trim();
        const holderName = cols[17]?.trim();

        if (!userCode || !beneficiaryName || !valueRaw) continue;

        const value = toSignedCurrency(valueRaw);
        if (value == null) continue;

        let serviceDate: string | null = null;
        if (dateRaw && dateRaw.length === 8) {
            const day = dateRaw.substring(0, 2);
            const month = dateRaw.substring(2, 4);
            const year = dateRaw.substring(4, 8);
            serviceDate = `${year}-${month}-${day}`;
        }

        copayEntries.push({
            type: 'COPAY',
            beneficiaryName,
            beneficiaryCpf: null,
            holderCpf: null,
            holderName,
            holderUserCode: null,
            userCode: normalizeUserCode(userCode),
            productCode: null,
            description: 'Coparticipação',
            procedure: `${procedureCode} - ${providerName}`,
            participation: null,
            quantity: null,
            percentage: null,
            additionalQuantity: null,
            filmArea: null,
            filmValue: null,
            serviceDate,
            serviceTime: null,
            value: value / 100,
            flag: null,
            raw: line,
        });
    }

    return copayEntries;
}

async function parseUnimedPremiumPdf(file: File, xlsx: typeof import('xlsx')): Promise<HealthPlanLifeRecord[]> {

    const textPages = await extractTextFromPdf(file);


    const lives: HealthPlanLifeRecord[] = [];
    let currentLife: HealthPlanLifeRecord | null = null;

    if (textPages.length > 0) {

    }

    let section: 'MAIN' | 'PROPORTIONAL' | 'EXCLUDED' = 'MAIN';
    const processedCredits = new Set<string>();

    for (const pageText of textPages) {
        const lines = pageText.split('\n');
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmed = line.trim();

            if (trimmed.includes('CLIENTES COM COBRANÇA PROPORCIONAL À DATA DE INCLUSÃO')) {
                if (currentLife) {
                    currentLife.totalCost = (currentLife.premiumTotal ?? 0) + (currentLife.copayAmount ?? 0);
                    lives.push(currentLife);
                }
                section = 'PROPORTIONAL';
                currentLife = null;
                continue;
            }
            if (trimmed.includes('CLIENTES EXCLUÍDOS')) {
                if (currentLife) {
                    currentLife.totalCost = (currentLife.premiumTotal ?? 0) + (currentLife.copayAmount ?? 0);
                    lives.push(currentLife);
                }
                section = 'EXCLUDED';
                currentLife = null;
                continue;
            }

            if (trimmed.toLowerCase().includes('relatório')) continue;
            if (trimmed.toLowerCase().includes('página')) continue;
            if (trimmed.toLowerCase().includes('resumo')) continue;
            if (trimmed.toLowerCase().includes('total')) continue;

            const codeMatch = trimmed.match(/(0006\d{13})/);

            let lifeMatch: RegExpMatchArray | null = null;
            if (codeMatch) {
                if (section === 'MAIN') {
                    // Enforce that the line starts with a Contract Number (or identifier) followed by the code
                    lifeMatch = trimmed.match(/^(\S+)\s+(0006\d{13})\s+(.+?)\s+(\d{2}\/\d{2}\/\d{4})/);
                } else {
                    // Proportional/Excluded: Starts with 0006... directly
                    lifeMatch = trimmed.match(/^(0006\d{13})\s+(.+?)\s+(\d{2}\/\d{2}\/\d{4})/);
                }
            }

            if (lifeMatch) {
                if (currentLife) {
                    currentLife.totalCost = (currentLife.premiumTotal ?? 0) + (currentLife.copayAmount ?? 0);
                    lives.push(currentLife);
                }

                let userCodeRaw, nameRaw, birthDateRaw;
                let familyIdentifier;

                if (section === 'MAIN') {
                    const rawCode = lifeMatch[2];
                    userCodeRaw = rawCode;
                    nameRaw = lifeMatch[3].trim();
                    birthDateRaw = lifeMatch[4];
                    const userCode = rawCode.substring(4, 16);
                    familyIdentifier = userCode.substring(0, userCode.length - 2);

                    // Check for Name Continuation
                    if (i + 1 < lines.length) {
                        const nextLine = lines[i + 1].trim();
                        const isNextLineHeader =
                            nextLine.toLowerCase().includes('relatório') ||
                            nextLine.toLowerCase().includes('página') ||
                            nextLine.toLowerCase().includes('codigo') ||
                            nextLine.toLowerCase().includes('código') ||
                            nextLine.toLowerCase().includes('taxa') ||
                            nextLine.toLowerCase().includes('descriçao') ||
                            nextLine.toLowerCase().includes('descrição') ||
                            nextLine.toLowerCase().includes('qtde') ||
                            nextLine.toLowerCase().includes('valor');

                        const hasDate = /\d{2}\/\d{2}\/\d{4}/.test(nextLine);
                        const hasMoney = /\d+,\d{2}/.test(nextLine);
                        const hasCode = /0006\d{13}/.test(nextLine);

                        if (nextLine && !isNextLineHeader && !hasDate && !hasMoney && !hasCode) {
                            nameRaw += ' ' + nextLine;
                            i++;
                        }
                    }
                } else {
                    // Proportional/Excluded
                    const rawCode = lifeMatch[1];
                    userCodeRaw = rawCode;
                    nameRaw = lifeMatch[2].trim();
                    birthDateRaw = lifeMatch[3];
                    const userCode = rawCode.substring(4, 16);
                    familyIdentifier = userCode.substring(0, userCode.length - 2);
                }

                const userCode = userCodeRaw.substring(4, 16);
                const isHolder = userCode.endsWith('00');

                // If EXCLUDED section, check if we already processed a credit for this user in MAIN
                if (section === 'EXCLUDED' && processedCredits.has(normalizeUserCode(userCode))) {
                    // Already handled in Main list
                    currentLife = null;
                    continue;
                }

                currentLife = {
                    beneficiaryName: nameRaw,
                    beneficiaryCpf: '',
                    holderCpf: '',
                    userCode: normalizeUserCode(userCode),
                    birthDate: section === 'MAIN' ? parseBradescoDate(birthDateRaw) : null,
                    relationship: isHolder ? 'Titular' : 'Dependente',
                    planName: 'Unimed', // Will be updated if specific plan lines found, or stay Unimed for adjustments
                    inclusionDate: section !== 'MAIN' ? parseBradescoDate(birthDateRaw) : null,
                    premiumTotal: 0,
                    copayAmount: null,
                    totalCost: 0,
                    plans: [],
                    copayEntries: [],
                    certificateCode: userCodeRaw,
                    familyIdentifier,
                    isPrimaryHolder: isHolder,
                    holderName: null,
                    holderUserCode: null
                };

                // If it's MAIN, we continue to look for plan lines.
                // If it's SECTION, the value is ON THIS LINE.
                // We fall through to the `if (currentLife)` block below which handles value extraction.
            }

            if (currentLife) {
                // Modified regex to look for parens around value: `(\(?[0-9]{1,3}(?:\.[0-9]{3})*,[0-9]{2}\)?)`
                const dateMatch = trimmed.match(/(\d{2}\/\d{2}\/\d{4})/);
                if (dateMatch && dateMatch.index && dateMatch.index > 0) {
                    let planPart = trimmed.substring(0, dateMatch.index).trim();
                    const afterDate = trimmed.substring(dateMatch.index + 10);

                    // Regex that allows optional parens for negative values: (123,45)
                    const moneyMatches = afterDate.match(/(\(?[0-9]{1,3}(?:\.[0-9]{3})*,[0-9]{2}\)?)/g);

                    if (moneyMatches && moneyMatches.length > 0) {
                        let valueRaw = moneyMatches[0];

                        // If there are multiple values, check if the last one is a parenthesized negative (credit)
                        // This handles cases like: 0,00 0,00 (249,71)
                        if (moneyMatches.length > 1) {
                            const lastVal = moneyMatches[moneyMatches.length - 1];
                            if (lastVal.startsWith('(') && lastVal.endsWith(')')) {
                                valueRaw = lastVal;
                            }
                        }

                        // Custom parse for (value)
                        let isNegative = false;
                        if (valueRaw.startsWith('(') && valueRaw.endsWith(')')) {
                            isNegative = true;
                            valueRaw = valueRaw.slice(1, -1); // Remove parens
                        }

                        let value = toSignedCurrency(valueRaw);

                        if (value !== null) {
                            if (isNegative || section === 'EXCLUDED') {
                                value = -Math.abs(value);
                            }

                            if (section === 'PROPORTIONAL') {
                                planPart = 'Cobrança Proporcional';
                            } else if (section === 'EXCLUDED') {
                                planPart = 'Crédito Exclusão';
                            } else if (section === 'MAIN' && isNegative) {
                                // Mark this user as having a processed credit
                                processedCredits.add(normalizeUserCode(currentLife.userCode ?? ''));
                            }

                            currentLife.premiumTotal = (currentLife.premiumTotal ?? 0) + value;

                            const newPlan = {
                                planName: planPart,
                                premiumTotal: value,
                                copayAmount: null
                            };

                            if (!currentLife.plans) {
                                currentLife.plans = [];
                            }

                            currentLife.plans.push(newPlan);

                            const uniqueNames = Array.from(new Set(currentLife.plans.map(p => p.planName)));
                            currentLife.planName = uniqueNames.join(' + ');
                        }
                    }
                }
            }
        }
    }

    if (currentLife) {
        currentLife.totalCost = (currentLife.premiumTotal ?? 0) + (currentLife.copayAmount ?? 0);
        lives.push(currentLife);
    }



    const holdersByFamily = new Map<string, HealthPlanLifeRecord>();
    lives.forEach(life => {
        if (life.isPrimaryHolder && life.familyIdentifier) {
            holdersByFamily.set(life.familyIdentifier, life);
        }
    });

    lives.forEach(life => {
        if (!life.isPrimaryHolder && life.familyIdentifier) {
            const holder = holdersByFamily.get(life.familyIdentifier);
            if (holder) {
                life.holderName = holder.beneficiaryName;
                life.holderUserCode = holder.userCode;
            }
        }
    });

    return lives;
}

// function generateLivesFromCopay removed per user request

function attachCopayEntriesToLives(lives: HealthPlanLifeRecord[], copayEntries: HealthPlanCopayRecord[]): HealthPlanLifeRecord[] {
    const livesWithCopay = lives.map((life) => ({
        ...life,
        totalCost: life.totalCost ?? (life.premiumTotal ?? 0) + (life.copayAmount ?? 0),
        copayEntries: [...(life.copayEntries ?? [])],
    }));

    const lifeByUserCode = new Map<string, HealthPlanLifeRecord>();
    livesWithCopay.forEach((life) => {
        const code = normalizeUserCode(life.userCode);
        if (!code) return;
        if (!lifeByUserCode.has(code)) {
            lifeByUserCode.set(code, life);
        }
    });

    copayEntries.forEach((entry) => {
        const entryCode = normalizeUserCode(entry.userCode);
        if (!entryCode) return;

        let life = lifeByUserCode.get(entryCode);

        // Fallback: Fuzzy match for Unimed discrepancies (TXT 12 chars vs PDF 17 chars)
        if (!life) {
            // Try to find a life whose code contains the entry code (TXT is usually shorter)
            for (const [lifeCode, l] of Array.from(lifeByUserCode.entries())) {
                if (lifeCode.includes(entryCode) || entryCode.includes(lifeCode)) {
                    life = l;
                    break;
                }
            }
        }

        // If life (beneficiary) not found by code, try to find by name if possible? 
        // Or if we generated lives from copay, it should be found.

        if (!life) {
            // Optional: Auto-create life here too if not found? 
            // But generateLivesFromCopay handles the "no lives at all" case.
            // If we have lives from Premium but this specific copay user is missing, strict mode says skip/audit.
            // For now, return.
            return;
        }

        if (!life.copayEntries) life.copayEntries = [];
        life.copayEntries.push(entry);
        // We accumulate copayAmount on the life record
        const val = entry.value ?? 0;
        life.copayAmount = (life.copayAmount ?? 0) + val;
        // Update plan breakdown check?
        if (life.plans && life.plans.length > 0) {
            life.plans[0].copayAmount = (life.plans[0].copayAmount ?? 0) + val;
        }
        life.totalCost = (life.premiumTotal ?? 0) + (life.copayAmount ?? 0);
    });

    return livesWithCopay;
}

export async function parseUnimedHealthPlanFiles(files: File[], password?: string, manualIof?: number): Promise<HealthPlanParseResult> {
    const xlsxModule = await loadXlsx();

    let txtCopayFile: File | undefined;
    let pdfPremiumFile: File | undefined;
    let monthlyFile: File | undefined;
    let copayEntries: HealthPlanCopayRecord[] = [];
    let debugRawPages: string[] | undefined;
    let lives: HealthPlanLifeRecord[] = [];

    // Totals tracking
    let totalDebit = 0;
    let totalCredit = 0;
    let totalDebitCount = 0;
    let totalCreditCount = 0;

    const addToTotals = (val: number | null) => {
        if (!val) return;
        if (val > 0) {
            totalDebit += val;
            totalDebitCount++;
        } else {
            totalCredit += val;
            totalCreditCount++;
        }
    };

    for (const file of files) {

        const name = file.name.toLowerCase();

        if (name.endsWith('.txt')) {
            const text = await readFileAsTextWithFallback(file);
            if (isUnimedCopayTxt(text)) {

                txtCopayFile = file;
                copayEntries = parseUnimedCopayTxt(text);
                // Note: Copays are usually positive values (debited from salary), but strictly in terms of "Provider Bill", they are debits to us?
                // Bradesco parser seems to only track premiums in totalDebit/Credit from PDF?
                // Let's stick to tracking premiums for now as the user asked for header consistency which usually refers to the bill analysis.
            } else {

            }
            continue;
        }

        if (name.endsWith('.pdf')) {
            try {
                // Pass password if needed (Unimed might use it in future, mostly for generic extraction)
                const textPages = await extractTextFromPdf(file, password);


                if (isUnimedPremiumPdf(textPages)) {
                    pdfPremiumFile = file;
                    // We need to inject the aggregation logic into parseUnimedPremiumPdf or calculate after?
                    // parseUnimedPremiumPdf returns lives. It doesn't return the raw entries for us to count.
                    // We should move the logic inside parseUnimedPremiumPdf or refactor.
                    // Since I cannot change the signature of the internal function easily without changing calls,
                    // I will refactor `parseUnimedPremiumPdf` to return totals OR I will calculate totals from the resulting structure?
                    // BUT, `totalDebitCount` needs individual entries. `HealthPlanLifeRecord` has `plans[]`.
                    // Each plan corresponds to an entry? Yes, in my parser logic I push to `plans`.
                    // So I can iterate over lives -> plans to calculate totals.

                    lives = await parseUnimedPremiumPdf(file, xlsxModule);

                    // Post-process lives to calculate totals matches Bradesco's granulatiry?
                    // Bradesco counts "entries" found in text.
                    // My Unimed parser creates a plan entry for each value found.
                    // So I can just sum up from lives.

                    lives.forEach(life => {
                        if (life.plans) {
                            life.plans.forEach(plan => {
                                addToTotals(plan.premiumTotal);
                            });
                        } else if (life.premiumTotal) {
                            // Fallback if no plans (shouldn't happen with current parser)
                            addToTotals(life.premiumTotal);
                        }
                    });

                } else {

                }
            } catch (e) {
                console.warn('Failed to sniff PDF', file.name, e);
            }
            continue;
        }

        if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
            monthlyFile = file;
        }
    }

    if (pdfPremiumFile || txtCopayFile || copayEntries.length > 0) {
        if (!pdfPremiumFile && monthlyFile) {
            // Legacy Excel Fallback for Lives
            const arrayBuffer = await monthlyFile.arrayBuffer();
            const workbook = xlsxModule.read(arrayBuffer, { type: 'array' });
            const livesSheetName = findSheet(workbook, ['relatório de vidas', 'vidas']);
            const livesRows = livesSheetName ? readRows(workbook.Sheets[livesSheetName], xlsxModule) : [];
            lives = consolidateLives(parseLives(livesRows, xlsxModule));

            // Calculate totals for Excel fallback
            lives.forEach(life => {
                addToTotals(life.premiumTotal);
            });
        }

        const livesWithCopay = attachCopayEntriesToLives(lives, copayEntries);
        const activeLives = livesWithCopay.filter((life) => !['EXCLUSÃO', 'EXCLUSAO'].includes((life.relationship || '').toUpperCase())).length;
        const premiumHealth = livesWithCopay.reduce((acc, life) => acc + (life.premiumTotal ?? 0), 0);

        const copayTotal = copayEntries.reduce((acc, entry) => acc + (entry.value ?? 0), 0);

        // Include Copays in the debit/credit totals
        copayEntries.forEach(entry => {
            addToTotals(entry.value);
        });

        // Total Cost = Premium + IOF (Financial Cost) + Copay
        // Bradesco includes IOF in totalCost.
        const totalCost = premiumHealth + copayTotal + (manualIof ?? 0);

        return {
            lives: livesWithCopay,
            movements: [],
            copayEntries,
            copaysAttached: true,
            totals: {
                premiumHealth: Number.isFinite(premiumHealth) ? premiumHealth : null,
                copayTotal: Number.isFinite(copayTotal) ? copayTotal : null,
                activeLives: Number.isFinite(activeLives) ? activeLives : null,
                totalCost: Number.isFinite(totalCost) ? totalCost : null,
                totalDebit: Number.isFinite(totalDebit) ? totalDebit : null,
                totalCredit: Number.isFinite(totalCredit) ? totalCredit : null,
                totalDebitCount: Number.isFinite(totalDebitCount) ? totalDebitCount : null,
                totalCreditCount: Number.isFinite(totalCreditCount) ? totalCreditCount : null,
                financialCost: Number.isFinite(manualIof) ? manualIof : null,
            },
            debugRawPages
        };
    }

    // Legacy Fallback (Excel Only)
    return {
        lives: [],
        movements: [],
        copayEntries: [],
        totals: {},
    }
}
